// =============================================================================
// MeuDriver — Booking Step 1: Date / Time / Modality
// =============================================================================

import { useEffect, useState } from 'react'
import { ChevronRight, RepeatIcon } from 'lucide-react'
import { format, addMinutes } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { useBookingStore } from '@/store/bookingStore'
import { formatCurrency, getStudentPrice } from '@/lib/utils'
import {
  DURATION_OPTIONS,
  TRIAL_DURATION_MINUTES,
  TRIAL_PRICE_MULTIPLIER,
} from '@/lib/constants'
import { useUsablePackagesForInstructor } from '@/hooks/usePackages'
import { useInstructorPackages } from '@/hooks/useLessonPackages'
import type { InstructorWithProfile } from '@/types/database'
import { InstructorSummaryCard } from './BookingFlowShared'
import { SlotPicker } from '@/components/student/SlotPicker'
import { RecurringBookingForm } from '@/components/student/RecurringBookingForm'

/** Aula de pacote: duração fixa (a mesma da RPC `book_package_lesson`). */
const PACKAGE_LESSON_MINUTES = 60

interface Props {
  instructor: InstructorWithProfile
  onNext: () => void
}

export function BookingStep1DateTime({ instructor, onNext }: Props) {
  const store = useBookingStore()
  const navigate = useNavigate()
  const [showRecurring, setShowRecurring] = useState(false)
  // Preço ao aluno (instrutor + 40 %) — esta tela só exibe valores.
  const hourlyRate = getStudentPrice(instructor.hourly_rate ?? 0)

  // Créditos de pacote do aluno com este instrutor (o que vence antes primeiro).
  const { data: myPackages, isLoading: loadingPackages } = useUsablePackagesForInstructor(instructor.id)
  const { data: instructorPackages } = useInstructorPackages(instructor.id)
  const sellsPackages = (instructorPackages ?? []).some((p) => p.is_active)
  const activePackage = myPackages[0] ?? null

  const modeOptions = [
    { key: 'single' as const, label: 'Aula avulsa', desc: 'Uma aula por vez' },
    { key: 'trial' as const, label: 'Aula experimental', desc: '30 min · 50% de desconto' },
    ...(activePackage
      ? [{
          key: 'package' as const,
          label: 'Usar meu pacote',
          desc: `${activePackage.lessons_remaining} aula${activePackage.lessons_remaining !== 1 ? 's' : ''} restante${activePackage.lessons_remaining !== 1 ? 's' : ''} · já paga`,
        }]
      : []),
  ]

  const isTrial = store.bookingMode === 'trial'
  const isPackage = store.bookingMode === 'package' && !!activePackage

  // Modo pacote sem crédito utilizável (depois de carregar) → volta para avulsa.
  useEffect(() => {
    if (loadingPackages) return
    if (store.bookingMode === 'package' && !activePackage) store.setBookingMode('single')
    if (isPackage && store.studentPackageId !== activePackage!.id) store.setStudentPackageId(activePackage!.id)
  }, [store, activePackage, isPackage, loadingPackages])

  const activeDuration = isTrial ? TRIAL_DURATION_MINUTES : store.durationMinutes
  const unitSubtotal = hourlyRate * (activeDuration / 60) * (isTrial ? TRIAL_PRICE_MULTIPLIER : 1)

  const canProceed = Boolean(store.selectedDate && store.selectedTime)

  return (
    <div className="space-y-6">
      <InstructorSummaryCard instructor={instructor} />

      {/* Toggle recorrente */}
      <button
        type="button"
        onClick={() => setShowRecurring((prev) => !prev)}
        className={`flex items-center gap-2 rounded-xl border-2 px-4 py-2.5 text-sm font-medium transition-all w-full sm:w-auto ${
          showRecurring
            ? 'border-brand-500 bg-brand-50 text-brand-700'
            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
        }`}
        data-testid="toggle-recurring-btn"
      >
        <RepeatIcon className="h-4 w-4" />
        Agendar recorrente (série de aulas)
      </button>

      {/* Formulário recorrente */}
      {showRecurring && (
        <Card className="border-brand-200 bg-brand-50/30">
          <CardContent className="p-5">
            <RecurringBookingForm
              instructorId={instructor.id}
              bookingType="single"
              onSuccess={(_seriesId, _count) => {
                navigate('/aluno/dashboard')
              }}
              onCancel={() => setShowRecurring(false)}
            />
          </CardContent>
        </Card>
      )}

      {/* Conteúdo normal — esconde se modo recorrente ativo */}
      {!showRecurring && (
        <>

      {/* Booking mode selector */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">Tipo de aula</Label>
        <div className={`grid grid-cols-1 gap-2 ${modeOptions.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
          {modeOptions.map((mode) => {
            const isSelected = store.bookingMode === mode.key
            return (
              <button
                key={mode.key}
                type="button"
                onClick={() => {
                  store.setBookingMode(mode.key)
                  store.setStudentPackageId(mode.key === 'package' ? activePackage!.id : null)
                  if (mode.key === 'trial') store.setDuration(TRIAL_DURATION_MINUTES)
                  else if (mode.key === 'package') store.setDuration(PACKAGE_LESSON_MINUTES)
                  else if (store.durationMinutes === TRIAL_DURATION_MINUTES) store.setDuration(60)
                }}
                className={`rounded-xl border-2 p-3 text-left transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <p className={`text-sm font-semibold ${isSelected ? 'text-primary' : 'text-gray-800'}`}>
                  {mode.label}
                  {mode.key === 'trial' && (
                    <span className="ml-1.5 inline-flex items-center rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-bold text-green-700">
                      50% OFF
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">{mode.desc}</p>
              </button>
            )
          })}
        </div>
      </div>

      {!activePackage && sellsPackages && (
        <p className="text-sm text-muted-foreground">
          Este instrutor vende pacotes de aulas.{' '}
          <Link to={`/instrutores/${instructor.slug ?? instructor.id}#pacotes`} className="font-medium text-primary hover:underline">
            Ver pacotes
          </Link>
        </p>
      )}

      {isPackage && (
        <Card className="bg-brand-50 border-brand-200">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-brand-800 mb-1">
              {activePackage!.instructor_package?.name ?? 'Pacote'} — aula de 1 hora
            </p>
            <p className="text-xs text-brand-700">
              Esta aula usa 1 crédito do seu pacote, sem nova cobrança. Restam{' '}
              {activePackage!.lessons_remaining} de {activePackage!.lessons_total}.
            </p>
          </CardContent>
        </Card>
      )}

      {isTrial && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-green-800 mb-1">Aula experimental — 30 minutos</p>
            <p className="text-xs text-green-700">
              Conheça o instrutor com 50% de desconto. Duração fixa de 30 min.
              Preço: {formatCurrency(unitSubtotal)}.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Seleção de data e horário via disponibilidade real do instrutor */}
      <SlotPicker instructorId={instructor.id} />

      {store.selectedTime && !isTrial && !isPackage && (
        <div className="space-y-3">
          <Label className="text-base font-semibold">Duração da aula</Label>
          <div className="flex flex-wrap gap-3">
            {DURATION_OPTIONS.map((opt) => {
              const isSelected = store.durationMinutes === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => store.setDuration(opt.value)}
                  className={`py-2 px-4 rounded-lg text-sm font-medium border transition-colors
                    ${isSelected
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary'
                    }`}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>

          {store.selectedDate && store.selectedTime && (
            <p className="text-sm text-muted-foreground">
              Término previsto: {(() => {
                try {
                  const start = new Date(`${format(store.selectedDate, 'yyyy-MM-dd')}T${store.selectedTime}:00`)
                  return format(addMinutes(start, store.durationMinutes), "HH'h'mm", { locale: ptBR })
                } catch { return '' }
              })()}
            </p>
          )}
        </div>
      )}

      {store.selectedTime && (
        <Card className="bg-gray-50">
          <CardContent className="p-4 flex justify-between items-center text-sm">
            <span className="text-muted-foreground">
              {isPackage
                ? '1 aula do pacote'
                : isTrial
                ? '30 min · 50% de desconto'
                : `${store.durationMinutes / 60}h × ${formatCurrency(hourlyRate)}/h`}
            </span>
            <span className="text-lg font-extrabold text-primary">
              {isPackage ? 'Já paga' : formatCurrency(unitSubtotal)}
            </span>
          </CardContent>
        </Card>
      )}

      <div className="pt-2">
        <Button size="lg" className="w-full sm:w-auto" disabled={!canProceed} onClick={onNext}>
          Próximo
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      </>
      )}
    </div>
  )
}
